import React, { useState, useEffect } from 'react';

const COLORS = ['#7132f5', '#149e61', '#d92d20', '#1DA1F2'];

function MiniLineChart({ dates, closes, color = '#7132f5', width = 320, height = 140 }) {
  if (!closes || closes.length < 2) return null;

  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;
  const padY = 10;
  const padX = 4;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;

  const points = closes.map((v, i) => {
    const x = padX + (i / (closes.length - 1)) * chartW;
    const y = padY + chartH - ((v - min) / range) * chartH;
    return `${x},${y}`;
  }).join(' ');

  const gradId = `grad-${color.replace('#', '')}`;

  // Area fill
  const areaPoints = `${padX},${padY + chartH} ${points} ${padX + chartW},${padY + chartH}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      {/* Start/end labels */}
      <text x={padX} y={height - 2} fontSize="9" fill="#9497a9" fontFamily="var(--mk-mono, monospace)">
        {dates[0]}
      </text>
      <text x={width - padX} y={height - 2} fontSize="9" fill="#9497a9" textAnchor="end" fontFamily="var(--mk-mono, monospace)">
        {dates[dates.length - 1]}
      </text>
    </svg>
  );
}

export default function ChartGrid({ symbols = [] }) {
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbols.length) { setLoading(false); return; }
    const unique = [...new Set(symbols)].slice(0, 4);
    fetch(`/api/chart-data?symbols=${unique.join(',')}&period=1mo`)
      .then(r => r.json())
      .then(data => { setChartData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [symbols]);

  const entries = Object.entries(chartData).slice(0, 4);

  if (loading) {
    return (
      <div className="chart-grid">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="chart-card chart-card--loading">
            <div className="chart-skeleton" />
          </div>
        ))}
      </div>
    );
  }

  if (!entries.length) return null;

  return (
    <div className="chart-grid">
      {entries.map(([symbol, data], i) => {
        const closes = data.closes || [];
        const first = closes[0] || 0;
        const last = closes[closes.length - 1] || 0;
        const change = first ? ((last - first) / first * 100) : 0;
        const color = COLORS[i % COLORS.length];

        return (
          <div key={symbol} className="chart-card">
            <div className="chart-card__header">
              <span className="chart-card__symbol">{symbol}</span>
              <span className="chart-card__price">
                {last.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className={`chart-card__change ${change >= 0 ? 'up' : 'down'}`}>
                {change >= 0 ? '▲' : '▼'}{Math.abs(change).toFixed(1)}%
              </span>
            </div>
            <MiniLineChart dates={data.dates || []} closes={closes} color={color} />
          </div>
        );
      })}
    </div>
  );
}
